import React, { useEffect, useMemo, useRef, useState } from 'react';
import ArcGraphHeader from './ArcGraphHeader';
import ArcCrewSummaryModal from './ArcCrewSummaryModal';
import TargetSettingsPanel from './TargetSettingsPanel';
import MetricTiles from './MetricTiles';
import ArcMetricConsole from './ArcMetricConsole';
import TimelineGrid from './TimelineGrid';
import StrokeSequenceBars from './StrokeSequenceBars';
import {
  buildAverageStroke,
  buildMetricMessage,
  buildSessionAverages,
  buildStrokeStatuses,
  buildStrokeTitles,
  buildCrewArcSummary,
  buildCrewMetricRanks,
  buildTargetHitRates,
  buildTargetValidationMessages,
  getStrokeMetricStatus,
  getTargetHitRateForMetric,
  deriveEffectiveLengthTargets,
  findNextFlaggedStrokeNumber,
  findPrevFlaggedStrokeNumber,
  formatPlaybackTime,
  getMetricDetails,
  getSeatStrokes,
  type MetricType,
} from './arcMetrics';
import { useArcTargets } from './useArcTargets';
import { useArcKeyboardControls } from './useArcKeyboardControls';
import RowingArcD3 from '../../../features/dashboards/RowingArcD3';
import {
  ArcPlaybackProvider,
  useArcPlayback,
} from '../../../features/dashboards/ArcPlaybackContext';
import type { StrokeSample } from '../../../features/dashboards/rowingArcTypes';
import { ARC_PHYSICS, SPEED_OPTIONS } from '../../../features/dashboards/arcConfig';
import { uiBtn } from '../../ui/buttonClasses';

/** When the arc page content area is at least this wide, show timeline + arc side-by-side. */
const TIMELINE_ARC_SIDE_BY_SIDE_MIN_PX = 1024;

interface ArcGraphCoachViewProps {
  sessionId?: string | null;
  sessionLabel?: string;
  strokes?: StrokeSample[];
  availableSeats?: number[];
  initialSeat?: number;
  strokeDurationMs?: number;
  embedded?: boolean;
}

interface ArcGraphCoachViewPlaybackProps {
  sessionId?: string | null;
  sessionLabel?: string;
  strokes: StrokeSample[];
  seatStrokes: StrokeSample[];
  selectedSeat: number;
  onSeatChange: (seat: number) => void;
  availableSeats: number[];
  embedded?: boolean;
}

type TransportHighlight = 'reset' | 'prev' | 'play' | 'next';

const ArcGraphCoachViewPlayback: React.FC<ArcGraphCoachViewPlaybackProps> = ({
  sessionId: _sessionId = null,
  sessionLabel,
  strokes,
  seatStrokes,
  selectedSeat,
  onSeatChange,
  availableSeats,
  embedded = false,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('catch');
  const [showAverageOverlay, setShowAverageOverlay] = useState(true);
  /** Which transport control shows the primary (blue) style; Play is default so idle state matches product expectation. */
  const [transportHighlight, setTransportHighlight] = useState<TransportHighlight>('play');
  const [crewSummaryOpen, setCrewSummaryOpen] = useState(false);
  const { targets, handleTargetChange, resetToDefaults } = useArcTargets();

  const {
    currentStrokeIndex,
    strokeCount,
    elapsedMs,
    elapsedMsRef,
    totalDurationMs,
    strokeDurationMs,
    isPlaying,
    togglePlay,
    playbackSpeed,
    setPlaybackSpeed,
    seekToStroke,
    reset,
  } = useArcPlayback();

  const strokeStatuses = useMemo(
    () => buildStrokeStatuses(seatStrokes, strokeCount, selectedMetric, targets),
    [seatStrokes, selectedMetric, strokeCount, targets]
  );
  const targetValidationMessages = useMemo(() => buildTargetValidationMessages(targets), [targets]);
  const targetHitRates = useMemo(
    () => buildTargetHitRates(seatStrokes, targets),
    [seatStrokes, targets]
  );
  const sessionInTargetPercent = useMemo(
    () => getTargetHitRateForMetric(selectedMetric, targetHitRates),
    [selectedMetric, targetHitRates]
  );
  const crewSummaries = useMemo(
    () => buildCrewArcSummary(strokes, targets, availableSeats),
    [strokes, targets, availableSeats]
  );
  const crewMetricRanks = useMemo(() => buildCrewMetricRanks(crewSummaries), [crewSummaries]);
  const averageStroke = useMemo(
    () => buildAverageStroke(seatStrokes, selectedSeat),
    [seatStrokes, selectedSeat]
  );
  const sessionAverages = useMemo(() => buildSessionAverages(seatStrokes), [seatStrokes]);

  const currentStrokeData =
    seatStrokes[Math.min(seatStrokes.length - 1, Math.max(0, currentStrokeIndex))];
  const currentMetricDetails = currentStrokeData
    ? getMetricDetails(selectedMetric, currentStrokeData, targets)
    : null;
  const currentMetricStatus = currentStrokeData
    ? getStrokeMetricStatus(selectedMetric, currentStrokeData, targets)
    : 'good';

  const currentMetricMessage = useMemo(
    () => buildMetricMessage(currentMetricDetails, currentMetricStatus, currentStrokeData),
    [currentMetricDetails, currentMetricStatus, currentStrokeData]
  );
  const prevFlaggedStrokeNumber = useMemo(
    () => findPrevFlaggedStrokeNumber(currentStrokeIndex, strokeStatuses),
    [currentStrokeIndex, strokeStatuses]
  );
  const nextFlaggedStrokeNumber = useMemo(
    () => findNextFlaggedStrokeNumber(currentStrokeIndex, strokeStatuses),
    [currentStrokeIndex, strokeStatuses]
  );
  const strokeTitles = useMemo(
    () => buildStrokeTitles(seatStrokes, selectedMetric, strokeCount),
    [seatStrokes, selectedMetric, strokeCount]
  );

  useArcKeyboardControls({
    currentStrokeIndex,
    strokeCount,
    togglePlay,
    seekToStroke,
  });

  const elapsedTime = formatPlaybackTime(elapsedMs);
  const totalTime = formatPlaybackTime(totalDurationMs);
  const currentStroke = currentStrokeIndex + 1;
  const canStepPrev = strokeCount > 0 && currentStrokeIndex > 0;
  const canStepNext = strokeCount > 0 && currentStrokeIndex < strokeCount - 1;

  const prevIsPlayingRef = useRef(isPlaying);
  useEffect(() => {
    if (isPlaying && !prevIsPlayingRef.current) {
      setTransportHighlight('play');
    }
    prevIsPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    if (transportHighlight === 'prev' && !canStepPrev) {
      setTransportHighlight('play');
    }
    if (transportHighlight === 'next' && !canStepNext) {
      setTransportHighlight('play');
    }
  }, [transportHighlight, canStepPrev, canStepNext]);

  const activeSeatStrokeIndex =
    seatStrokes.length > 0 ? Math.min(seatStrokes.length - 1, Math.max(0, currentStrokeIndex)) : 0;
  const effectiveLengthTargets = useMemo(() => deriveEffectiveLengthTargets(targets), [targets]);

  const layoutWidthRef = useRef<HTMLDivElement>(null);
  const [timelineArcSideBySide, setTimelineArcSideBySide] = useState(false);

  useEffect(() => {
    const el = layoutWidthRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const update = () => {
      setTimelineArcSideBySide(el.clientWidth >= TIMELINE_ARC_SIDE_BY_SIDE_MIN_PX);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      className={
        embedded
          ? 'w-full'
          : 'min-h-screen bg-surface px-3 py-4 sm:px-5 sm:py-5 md:px-8 md:py-6 lg:px-10'
      }
    >
      <div
        ref={layoutWidthRef}
        className={`${embedded ? 'w-full min-w-0 space-y-4 sm:space-y-5' : 'mx-auto w-full min-w-0 max-w-[1400px] space-y-4 sm:space-y-5'}`}
      >
        <ArcGraphHeader
          currentStroke={currentStroke}
          totalStrokes={strokeCount}
          selectedSeat={selectedSeat}
          availableSeats={availableSeats}
          onSeatChange={onSeatChange}
          elapsedTime={elapsedTime}
          totalTime={totalTime}
          onOpenCrewSummary={() => setCrewSummaryOpen(true)}
          isPlaying={isPlaying}
          onTogglePlay={togglePlay}
          onReset={() => {
            setTransportHighlight('reset');
            reset();
          }}
          onStepPrev={() => seekToStroke(Math.max(0, currentStrokeIndex - 1))}
          onStepNext={() => seekToStroke(Math.min(strokeCount - 1, currentStrokeIndex + 1))}
          canStepPrev={canStepPrev}
          canStepNext={canStepNext}
          transportHighlight={transportHighlight}
          onTransportHighlight={setTransportHighlight}
          playbackSpeed={playbackSpeed}
          speedOptions={SPEED_OPTIONS}
          onPlaybackSpeedChange={setPlaybackSpeed}
          nextFlaggedStrokeNumber={nextFlaggedStrokeNumber}
          onJumpToNextIssue={() => {
            if (nextFlaggedStrokeNumber != null) {
              seekToStroke(nextFlaggedStrokeNumber - 1);
            }
          }}
        />

        <ArcCrewSummaryModal
          isOpen={crewSummaryOpen}
          onClose={() => setCrewSummaryOpen(false)}
          summaries={crewSummaries}
          ranks={crewMetricRanks}
          targets={targets}
          sessionLabel={sessionLabel}
          selectedSeat={selectedSeat}
          onSelectSeat={onSeatChange}
          onFocusSeatMetric={(seat, metric) => {
            onSeatChange(seat);
            setSelectedMetric(metric);
          }}
        />

        <TargetSettingsPanel
          targets={targets}
          onTargetChange={handleTargetChange}
          onResetToDefaults={resetToDefaults}
          validationMessages={targetValidationMessages}
        />

        <MetricTiles
          seatStrokes={seatStrokes}
          activeStrokeIndex={activeSeatStrokeIndex}
          targets={{
            catchAngle: { min: targets.catchAngleMin, max: targets.catchAngleMax },
            finishAngle: { min: targets.finishAngleMin, max: targets.finishAngleMax },
            catchGap: { max: targets.catchGapMax },
            finishSlip: { min: targets.finishSlipMin, max: targets.finishSlipMax },
          }}
        />

        {/* Narrow content ⇒ stacked (timeline above arc); wide ⇒ arc | timeline */}
        <div
          className={`grid w-full min-w-0 gap-4 sm:gap-5 ${
            timelineArcSideBySide ? 'grid-cols-12 items-start' : 'grid-cols-1'
          }`}
        >
          <div
            className={`flex min-w-0 flex-col gap-3 ${
              timelineArcSideBySide ? 'col-span-4 col-start-9 row-start-1' : ''
            }`}
          >
            <ArcMetricConsole
              selectedMetric={selectedMetric}
              status={currentMetricStatus}
              strokeNumber={currentStroke}
              detail={currentMetricMessage.detail}
              sessionInTargetPercent={sessionInTargetPercent}
              prevFlaggedStrokeNumber={prevFlaggedStrokeNumber}
              nextFlaggedStrokeNumber={nextFlaggedStrokeNumber}
              onJumpToStroke={(strokeNumber) => seekToStroke(strokeNumber - 1)}
            />
            <div className="min-w-0 rounded-lg border border-border bg-surface p-3 shadow-sm sm:p-4">
              <TimelineGrid
                totalStrokes={strokeCount}
                currentStroke={currentStroke}
                strokeStatuses={strokeStatuses}
                strokeTitles={strokeTitles}
                onStrokeClick={(strokeNum) => {
                  seekToStroke(strokeNum - 1);
                }}
                selectedMetric={selectedMetric}
                onMetricChange={setSelectedMetric}
              />
            </div>
          </div>

          <div
            className={`min-w-0 rounded-lg border border-border bg-surface p-3 shadow-sm sm:p-4 ${
              timelineArcSideBySide ? 'col-span-8 col-start-1 row-start-1' : ''
            }`}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-rowing-gray-900">
                Seat {selectedSeat} Arc
              </h3>
              <button
                type="button"
                onClick={() => setShowAverageOverlay((previous) => !previous)}
                className={uiBtn('toggle', { pressed: showAverageOverlay, size: 'sm' })}
              >
                {showAverageOverlay ? 'Average overlay on' : 'Average overlay off'}
              </button>
            </div>
            <div
              className="relative flex w-full items-center justify-center"
              style={{ minHeight: '360px', padding: '0 12px 4px' }}
            >
              {strokes && strokes.length > 0 ? (
                <div className="flex h-full w-full items-center justify-center">
                  <RowingArcD3
                    strokes={seatStrokes}
                    activeStrokeIndex={activeSeatStrokeIndex}
                    playbackElapsedMsRef={elapsedMsRef}
                    strokeDurationMs={strokeDurationMs}
                    isPlaying={isPlaying}
                    embedded
                    targetGuides={{
                      catchMin: targets.catchAngleMin,
                      catchMax: targets.catchAngleMax,
                      finishMin: targets.finishAngleMin,
                      finishMax: targets.finishAngleMax,
                    }}
                    ghostStroke={showAverageOverlay ? averageStroke : null}
                  />
                </div>
              ) : (
                <div className="text-center text-rowing-gray-500">
                  <p>No stroke data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <StrokeSequenceBars
          sessionAverages={sessionAverages}
          seatStrokes={seatStrokes}
          activeStrokeIndex={activeSeatStrokeIndex}
          onStrokeClick={(strokeIndex) => seekToStroke(strokeIndex)}
          targets={{
            catchGapMax: targets.catchGapMax,
            finishSlipMin: targets.finishSlipMin,
            finishSlipMax: targets.finishSlipMax,
            effectiveLengthMin: effectiveLengthTargets.min,
            effectiveLengthMax: effectiveLengthTargets.max,
            warningBand: targets.warningBand,
          }}
        />
      </div>
    </div>
  );
};

const ArcGraphCoachView: React.FC<ArcGraphCoachViewProps> = ({
  sessionId = null,
  sessionLabel,
  strokes = [],
  availableSeats = [1, 2, 3, 4, 5, 6, 7, 8],
  initialSeat = 5,
  strokeDurationMs: strokeDurationMsProp,
  embedded = false,
}) => {
  const [selectedSeat, setSelectedSeat] = useState(initialSeat);

  const seatStrokes = useMemo(() => getSeatStrokes(strokes, selectedSeat), [strokes, selectedSeat]);

  const strokeDurationMs = strokeDurationMsProp ?? ARC_PHYSICS.STROKE_DURATION_MS;

  useEffect(() => {
    setSelectedSeat(initialSeat);
  }, [initialSeat]);

  useEffect(() => {
    if (availableSeats.length > 0 && !availableSeats.includes(selectedSeat)) {
      setSelectedSeat(availableSeats[0]);
    }
  }, [availableSeats, selectedSeat]);

  return (
    <ArcPlaybackProvider
      key={`${sessionId ?? 'offline'}-${selectedSeat}`}
      strokeCount={Math.max(1, seatStrokes.length)}
      strokeDurationMs={strokeDurationMs}
      initialPlaying={false}
      loop
    >
      <ArcGraphCoachViewPlayback
        sessionId={sessionId}
        sessionLabel={sessionLabel}
        strokes={strokes}
        seatStrokes={seatStrokes}
        selectedSeat={selectedSeat}
        onSeatChange={setSelectedSeat}
        availableSeats={availableSeats}
        embedded={embedded}
      />
    </ArcPlaybackProvider>
  );
};

export default ArcGraphCoachView;
